import React from "react";
import { UserRole } from "@modules/authentication/roles";
import { Badge } from "~/components/ui/badge";
import { cn } from "~/lib/utils";
import type { PagedUsersResult } from "@modules/user/user.service";
import { displayUserName } from "@modules/user/deletedUser";

export interface OrganisationMembershipViewModel {
  id: string;
  name: string;
}

export interface UserViewModel {
  id: string;
  email?: string | null;
  fullName?: string | null;
  createdAt: string;
  role: UserRole;
  organisationMemberships: OrganisationMembershipViewModel[];
}

type BadgeStyleProps = Pick<
  React.ComponentProps<typeof Badge>,
  "variant" | "className"
>;

export const getRoleBadgeProps = (role: UserRole): BadgeStyleProps => {
  if (role === UserRole.SuperAdmin) {
    return {
      className: cn(
        "border-0 text-[#253974]",
        "bg-[linear-gradient(135deg,_#7C8BFF_0%,_#E05CCB_100%)]"
      ),
    };
  }

  if (role === UserRole.User) {
    return { variant: "secondary" };
  }

  return { variant: "default" };
};

export const toRole = (roleValues?: readonly string[] | null): UserRole => {
  if (!roleValues || roleValues.length === 0) {
    return UserRole.User;
  }

  if (roleValues.includes(UserRole.SuperAdmin)) {
    return UserRole.SuperAdmin;
  }

  if (roleValues.includes(UserRole.Admin)) {
    return UserRole.Admin;
  }

  return UserRole.User;
};

export const toUserViewModel = (
  user: PagedUsersResult["users"][number]
): UserViewModel => ({
  id: user.id,
  email: user.email,
  fullName: user.fullName,
  createdAt: user.createdAt,
  role: toRole(user.roles),
  organisationMemberships:
    user.organisationMemberships?.filter(Boolean).map((organisation) => ({
      id: organisation.id,
      name: organisation.name,
    })) ?? [],
});

export const getUserDisplayName = (
  user: Pick<UserViewModel, "fullName" | "email">,
  fallback = "this user"
) => displayUserName(user, fallback);
